/**
 * Single condition row for List Tag Rule form.
 *
 * With no `scope` (or the default one) this renders exactly as it always has —
 * that equivalence is what keeps the existing override specs meaningful. Given a
 * country/campaign scope it swaps the "Default value" input for one that writes
 * that scope's override, and folds the full tables into a disclosure.
 */

import {
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Trash2 } from 'lucide-react';

import { Button } from '@/components/ui';
import {
  clearValueForScope,
  DEFAULT_SCOPE,
  isDefaultScope,
  resolveForScope,
  setValueForScope,
  type TagRuleScope,
} from '@/features/tag-rule-scope';
import type {
  TagRuleCampaignThreshold,
  TagRuleCondition,
  TagRuleCountryThreshold,
} from '@/hooks';
import { describeCompareTo } from '@/features/tag-rule-compare/compare-window';
import { cn } from '@/lib/utils';

import { CampaignThresholdOverrides } from './campaign-overrides';
import { CountryThresholdOverrides } from './country-overrides';
import {
  AllScopesDisclosure,
  DefaultValueField,
  MetricOperatorFields,
  ScopedValueField,
} from './parts';

const EMPTY_THRESHOLDS: TagRuleCountryThreshold[] = [];
const EMPTY_CAMPAIGN_THRESHOLDS: TagRuleCampaignThreshold[] = [];

interface ConditionRowProps {
  condition: TagRuleCondition;
  onChange: (c: TagRuleCondition) => void;
  onRemove: () => void;
  canRemove: boolean;
  /** Country/campaign being edited. Omitted = the plain default view. */
  scope?: TagRuleScope;
  /** Selectable metric fields; defaults to the list stats columns. */
  fields?: ReadonlyArray<{ key: string; label: string }>;
  /** Extra control rendered beside the bin (e.g. the FO compare toggle). */
  action?: ReactNode;
}

export const ConditionRow: FC<ConditionRowProps> = ({
  condition,
  onChange,
  onRemove,
  canRemove,
  scope,
  fields,
  action,
}) => {
  const [localValue, setLocalValue] = useState(String(condition.value));
  const lastSentValue = useRef(condition.value);

  useEffect(() => {
    if (lastSentValue.current !== condition.value) {
      lastSentValue.current = condition.value;
      setLocalValue(String(condition.value));
    }
  }, [condition.value]);

  const handleValueChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      setLocalValue(raw);
      if (raw === '') {
        lastSentValue.current = 0;
        onChange({ ...condition, value: 0 });
      } else {
        const num = Number(raw);
        if (!Number.isNaN(num)) {
          lastSentValue.current = num;
          onChange({ ...condition, value: num });
        }
      }
    },
    [condition, onChange],
  );

  const thresholds = condition.countryThresholds ?? EMPTY_THRESHOLDS;

  const setThresholds = useCallback(
    (next: typeof thresholds) => {
      onChange({
        ...condition,
        countryThresholds: next.length > 0 ? next : undefined,
      });
    },
    [condition, onChange],
  );

  const campaignThresholds = condition.campaignThresholds ?? EMPTY_CAMPAIGN_THRESHOLDS;

  const setCampaignThresholds = useCallback(
    (next: typeof campaignThresholds) => {
      onChange({
        ...condition,
        campaignThresholds: next.length > 0 ? next : undefined,
      });
    },
    [condition, onChange],
  );

  const scoped = !isDefaultScope(scope);
  const resolved = useMemo(
    () => resolveForScope(condition, scope ?? DEFAULT_SCOPE),
    [condition, scope],
  );

  const overrideTables = (
    <>
      <CountryThresholdOverrides rows={thresholds} onChange={setThresholds} fields={fields} />
      <CampaignThresholdOverrides
        rows={campaignThresholds}
        onChange={setCampaignThresholds}
        fields={fields}
      />
    </>
  );

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-muted/20">
      <div
        className={cn(
          // items-START, not center: the scoped Value cell carries a hint line
          // under its input, so centering made that cell taller and floated its
          // label and input ABOVE Metric and Operator. Aligning tops keeps every
          // label on one baseline whatever sits underneath.
          'grid items-start gap-3 px-4 py-3',
          canRemove || action
            ? 'grid-cols-[1fr_auto_auto_auto] sm:grid-cols-[minmax(140px,1fr)_80px_100px_auto]'
            : 'grid-cols-[1fr_auto_auto] sm:grid-cols-[minmax(140px,1fr)_80px_100px]',
        )}
      >
        <MetricOperatorFields condition={condition} onChange={onChange} fields={fields} />
        {scoped && scope ? (
          <ScopedValueField
            value={resolved.value}
            defaultValue={resolved.defaultValue}
            source={resolved.source}
            onChange={(v) => onChange(setValueForScope(condition, scope, v))}
            onReset={() => onChange(clearValueForScope(condition, scope))}
            compareNote={
              resolved.compareTo
                ? describeCompareTo(
                    resolved.compareTo,
                    (key) => fields?.find((f) => f.key === key)?.label ?? key,
                  )
                : undefined
            }
          />
        ) : (
          <DefaultValueField value={localValue} onChange={handleValueChange} />
        )}
        {/* pt-5 clears the 12px label + 4px margin above every input, so the
            bin lines up with the fields rather than their labels. */}
        {(canRemove || action) && (
          <div className="flex items-start gap-1 pt-5">
            {action}
            {canRemove && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={onRemove}
                className="h-9 w-9 shrink-0 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                aria-label="Remove condition"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      </div>
      {scoped ? (
        <AllScopesDisclosure
          countryCount={thresholds.filter((t) => t.countryKey.trim()).length}
          campaignCount={campaignThresholds.filter((t) => t.campaignId.trim()).length}
        >
          {overrideTables}
        </AllScopesDisclosure>
      ) : (
        overrideTables
      )}
    </div>
  );
};
